<template>
  <nav v-if="totalItems" class="pagination-controls flex justify-between" aria-label="Pagination">
    <button
      class="primary-focus"
      :class="onFirstPage ? 'cursor-not-allowed' : 'cursor-pointer'"
      aria-label="Previous page"
      :tabindex="onFirstPage ? -1 : 0"
      :disabled="onFirstPage"
      data-test-id="previous-arrow"
      @click="handlePrevArrowClick"
    >
      <BackSmallIcon :width="24" :height="24" :color="onFirstPage ? '#B4B5B7' : '#4B4C52'" />
    </button>
    <div class="flex justify-center" :class="totalPages < 5 ? 'gap-24' : 'gap-8'">
      <div v-for="(page, index) in visiblePages" :key="index">
        <div v-if="page === '...'" class="text-grey-clickable pt-8">{{ page }}</div>
        <button
          v-else
          class="pagination-controls__page-btn primary-focus rounded-xl border-1 border-solid border-grey-divider-contrast text-grey-clickable px-4 min-w-32 h-32"
          :class="{ active: page === currentPage }"
          :aria-current="page === currentPage"
          :aria-label="`Page ${page}`"
          @click="handlePageBtnClick(page)"
        >
          {{ page }}
        </button>
      </div>
    </div>
    <button
      class="primary-focus"
      :class="onLastPage ? 'cursor-not-allowed' : 'cursor-pointer'"
      aria-label="Next page"
      :tabindex="onLastPage ? -1 : 0"
      :disabled="onLastPage"
      data-test-id="next-arrow"
      @click="handleNextArrowClick"
    >
      <DisclosureSmallIcon :width="24" :height="24" :color="onLastPage ? '#B4B5B7' : '#4B4C52'" />
    </button>
  </nav>
</template>

<script lang="ts">
import BackSmallIcon from '../../graphics/icons/BackSmallIcon.vue';
import DisclosureSmallIcon from '../../graphics/icons/DisclosureSmallIcon.vue';

export default {
  name: 'PaginationControls',
  components: { BackSmallIcon, DisclosureSmallIcon },
  props: {
    totalItems: {
      type: Number,
      default: null,
    },
    itemsPerPage: {
      type: Number,
      required: true,
    },
    startingPage: {
      type: Number,
      required: false,
      default: 1,
    },
  },
  data(): any {
    return {
      currentPage: this.startingPage,
    };
  },
  computed: {
    totalPages(): number {
      return Math.ceil(this.totalItems / this.itemsPerPage);
    },
    visiblePages(): any[] {
      if (this.totalPages < 6) {
        return Array.from({ length: this.totalPages }, (_, i) => i + 1);
      }

      const pages = [] as any;
      if (this.currentPage < 4) {
        // [ 1 2 [3] 4 ... 10 ]
        pages.push(...Array.from({ length: 4 }, (_, i) => i + 1));
        pages.push('...', this.totalPages);
      } else if (this.currentPage > this.totalPages - 3) {
        // [ 1 ... 7 [8] 9 10 ]
        pages.push(1, '...');
        pages.push(...Array.from({ length: 4 }, (_, i) => this.totalPages - 3 + i));
      } else {
        // [ 1 ... 4 [5] 6 ... 10 ]
        pages.push(1, '...');
        pages.push(this.currentPage - 1, this.currentPage, this.currentPage + 1);
        pages.push('...', this.totalPages);
      }
      return pages;
    },
    onFirstPage(): boolean {
      return this.currentPage === 1;
    },
    onLastPage(): boolean {
      return this.currentPage === this.totalPages;
    },
  },
  watch: {
    currentPage(): void {
      this.$emit('pageChange', this.currentPage);
    },
    startingPage(newVal): void {
      this.currentPage = newVal;
    },
  },
  methods: {
    handlePrevArrowClick() {
      if (!this.onFirstPage) {
        this.currentPage -= 1;
      }
    },
    handleNextArrowClick() {
      if (!this.onLastPage) {
        this.currentPage += 1;
      }
    },
    handlePageBtnClick(page) {
      this.currentPage = page;
    },
  },
};
</script>

<style lang="scss" scoped>
.pagination-controls {
  &__page-btn {
    &.active {
      background-color: #4b4c52;
      border: solid 1px #4b4c52;
      color: #ffffff;
    }
    &:hover {
      background-color: #d7d8d9;
      border: solid 1px #bb0049;
    }
  }
}
</style>
